import { FAVORITES_MODE, FAVORITES_STORAGE, FAVORITES_SETTINGS } from '/settings.js';
import { clearAllSpots, clearSearch } from '../map/map-visibility.js';
import { getText, getCurrentLanguage } from '../translations/translations.js';
import { buildUrlForIds, parseUrlParams } from './url-utils.js';
import { getSpotsByFavorites } from './spot-queries.js';
import { clearAllUI } from './clearAll.js';
import { setCardsContext } from '../cards/index.js';
import { displaySpots } from '../map/map.js';
import { updateMapView } from '../map/map-transitions.js';
import { confirmDelete, info } from '../components/alerts.js';
import { getLastSelectedAreaSlug, getAreaBySlug } from '../area-selector/index.js';

const LOCAL_KEY = 'favorites';

let favoritesToastEl = null;
let favoritesToastTimeout = null;

function showFavoritesToast(message) {
    if (!message) return;
    if (!favoritesToastEl) {
        favoritesToastEl = document.createElement('div');
        favoritesToastEl.className = 'favorites-toast';
        document.body.appendChild(favoritesToastEl);
    }
    favoritesToastEl.textContent = message;
    favoritesToastEl.classList.add('show');

    if (favoritesToastTimeout) {
        clearTimeout(favoritesToastTimeout);
    }
    favoritesToastTimeout = setTimeout(() => {
        if (favoritesToastEl) {
            favoritesToastEl.classList.remove('show');
        }
    }, 1600);
}

// Reads favorites from storage and normalizes them to { id, area_id } format
function getLocalFavorites() {
    try {
        const raw = JSON.parse(localStorage.getItem(LOCAL_KEY)) || [];
        // Normalize: handle legacy array of IDs
        return raw.map(item => {
            if (typeof item === 'number' || typeof item === 'string') {
                return { id: parseInt(item), area_id: null };
            }
            return item;
        });
    } catch {
        return [];
    }
}

function setLocalFavorites(favs) {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(favs));
}

export function isFavorite(spotId) {
    if (FAVORITES_MODE === FAVORITES_STORAGE.LOCAL) {
        const favs = getLocalFavorites();
        return favs.some(f => f.id === parseInt(spotId));
    }
    // Future: handle MySQL
    return false;
}

export async function toggleFavorite(spotId) {
    if (window.LOGGING && window.LOGGING.ENABLED) {
        console.log('[FAVORITES] toggleFavorite called for spot:', spotId);
    }
    if (FAVORITES_MODE === FAVORITES_STORAGE.LOCAL) {
        let favs = getLocalFavorites();
        const id = parseInt(spotId);
        const existingIndex = favs.findIndex(f => f.id === id);
        const isAdding = existingIndex === -1;

        if (existingIndex !== -1) {
            // Remove
            favs.splice(existingIndex, 1);
        } else {
            // Add - Get current Area ID first
            let areaId = null;
            const currentSlug = getLastSelectedAreaSlug();
            if (currentSlug) {
                const area = await getAreaBySlug(currentSlug);
                if (area) {
                    areaId = parseInt(area.id);
                }
            }

            // Check favorites count PER AREA (not total)
            const favsForThisArea = favs.filter(f => f.area_id === areaId);
            if (favsForThisArea.length >= FAVORITES_SETTINGS.MAX_SPOTS_TO_SAVE) {
                alert(`You can only save up to ${FAVORITES_SETTINGS.MAX_SPOTS_TO_SAVE} favorites per area!`);
                return false;
            }

            favs.push({ id: id, area_id: areaId });
        }

        setLocalFavorites(favs);

        const toastKey = isAdding ? 'messages.favoritesAdded' : 'messages.favoritesRemoved';
        const toastText = await getText(toastKey);
        showFavoritesToast(toastText);

        // Dispatch event with list of IDs for compatibility
        const ids = favs.map(f => f.id);
        window.dispatchEvent(new CustomEvent('favoritesChanged', { detail: ids }));

        return isFavorite(spotId);
    }
    // Future: handle MySQL
    return false;
}

export function getFavorites() {
    if (FAVORITES_MODE === FAVORITES_STORAGE.LOCAL) {
        return getLocalFavorites().map(f => f.id);
    }
    // Future: handle MySQL
    return [];
}

export function setFavorites(ids) {
    // Warning: This overrides everything, likely used for clearing.
    // If setting specific IDs, we lose area info unless we fetch it.
    // For "Delete All", ids is [].
    if (FAVORITES_MODE === FAVORITES_STORAGE.LOCAL) {
        // If ids is simple array, map to objects with null area (or handle appropriately)
        // But typically setFavorites is used to CLEAR.
        if (ids.length === 0) {
            setLocalFavorites([]);
        } else {
            // If restoring IDs, we don't know area.
            const newFavs = ids.map(id => ({ id: parseInt(id), area_id: null }));
            setLocalFavorites(newFavs);
        }
        window.dispatchEvent(new CustomEvent('favoritesChanged', { detail: ids }));
    }
    // Future: handle MySQL
}

/**
 * Deletes all favorites for a specific area
 * @param {number} areaId - The area ID to delete favorites for
 * @returns {number} Number of favorites deleted
 */
export function deleteFavoritesForArea(areaId) {
    if (FAVORITES_MODE === FAVORITES_STORAGE.LOCAL) {
        const favs = getLocalFavorites();
        const beforeCount = favs.length;
        // Filter out favorites that match this area
        const remainingFavs = favs.filter(f => f.area_id !== parseInt(areaId));
        setLocalFavorites(remainingFavs);

        // Dispatch event with remaining IDs
        const remainingIds = remainingFavs.map(f => f.id);
        window.dispatchEvent(new CustomEvent('favoritesChanged', { detail: remainingIds }));

        return beforeCount - remainingFavs.length;
    }
    // Future: handle MySQL
    return 0;
}

/**
 * Fetches favorites using the centralized spot querying system
 * @param {Array} favIds - Array of favorite IDs
 * @returns {Promise<Array>} Array of all favorite spots
 */
export async function fetchFavoritesWithChunking(favIds) {
    if (favIds.length === 0) return [];
    // Use the centralized spot querying system for fetching favorites
    return await getSpotsByFavorites(favIds);
}

let globalFavoritesBtn = null;
let globalSetFavoritesInactive = null;
let globalFavoritesChangedHandler = null; // Reference for cleanup

export function initFavoritesButton({ buttonId, mapApi }) {
    const favBtn = document.getElementById(buttonId);
    if (!favBtn || !mapApi) return;
    let showingFavorites = false;

    // Clean up previous listener if it exists
    if (globalFavoritesChangedHandler) {
        window.removeEventListener('favoritesChanged', globalFavoritesChangedHandler);
        globalFavoritesChangedHandler = null;
    }

    function updateButton() {
        if (showingFavorites) {
            favBtn.classList.add('active');
        } else {
            favBtn.classList.remove('active');
        }
    }

    // Make global reset available
    globalFavoritesBtn = favBtn;
    globalSetFavoritesInactive = function () {
        showingFavorites = false;
        updateButton();
    };

    function updateUrlWithFavorites(favorites) {
        const newUrl = buildUrlForIds(favorites);
        window.history.replaceState({}, '', newUrl);
    }

    // Helper to get IDs for the current area from local storage
    async function getFilteredFavoriteIds() {
        let currentAreaSlug = getLastSelectedAreaSlug();

        // Fallback to URL if local storage is empty
        if (!currentAreaSlug) {
            const { areaSlug } = parseUrlParams();
            currentAreaSlug = areaSlug;
        }

        if (!currentAreaSlug) {
            return []; // No area selected? maybe return all? Or empty.
        }

        const currentArea = await getAreaBySlug(currentAreaSlug);
        if (!currentArea) {
            return [];
        }

        const favs = getLocalFavorites();

        // Filter items that match the current area ID
        const filtered = favs
            .filter(f => f.area_id === parseInt(currentArea.id))
            .map(f => f.id);

        return filtered;
    }

    favBtn.addEventListener('click', async () => {
        clearAllUI(); // Clears popups etc, but not spots
        showingFavorites = !showingFavorites;
        updateButton();

        // Clear spot selection when entering/exiting favorites view
        window.dispatchEvent(new CustomEvent('spotCleared'));

        // When showing favorites, we only want the ones for the CURRENT AREA
        // We fetch based on the filtered list
        let displayIds = [];
        if (showingFavorites) {
            displayIds = await getFilteredFavoriteIds();
        } else {
            // When turning off, we clear URL
            displayIds = [];
        }

        // Update URL - Should URL show ALL favorites or just filtered?
        // If I share the URL, I might want to share ALL my favorites?
        // But for consistency with the view, let's use the filtered IDs or just empty?
        // "favorites" param usually takes list of IDs.
        // Let's update URL with the ones we are showing.
        updateUrlWithFavorites(displayIds);

        if (showingFavorites) {
            try {
                // Pass true for animate to respect ONE SOURCE OF TRUTH
                setCardsContext('special', { specialType: 'favorites' }, true);
            } catch (err) {
                console.warn('Could not set favorites special cards context:', err);
            }

            if (displayIds.length === 0) {
                // Always show area-specific message, even if no favorites at all
                let currentAreaSlug = getLastSelectedAreaSlug();
                if (!currentAreaSlug) {
                    const { areaSlug } = parseUrlParams();
                    currentAreaSlug = areaSlug;
                }

                let areaName = currentAreaSlug || 'this area';
                if (currentAreaSlug) {
                    const currentArea = await getAreaBySlug(currentAreaSlug);
                    if (currentArea) {
                        const currentLang = getCurrentLanguage();
                        areaName = currentArea[`name_${currentLang}`] || currentArea.name_en || currentAreaSlug;
                    }
                }

                const title = await getText('messages.noFavorites'); // "No favorited spots"
                let message = await getText('alerts.messages.noFavoritesForArea');
                if (message && message.includes('{area}')) {
                    message = message.replace('{area}', areaName);
                } else {
                    // Fallback if translation doesn't exist
                    message = `You have no favorited spots for ${areaName}`;
                }

                await info({
                    title: title,
                    message: message,
                    onConfirm: () => cleanupFavoritesView(),
                    onCancel: () => cleanupFavoritesView()
                });
                return;
            }

            // Fetch only the filtered spots
            let spots = await fetchFavoritesWithChunking(displayIds);
            await updateMapView({ spotsToDisplay: spots, shouldAnimate: true });
        } else {
            // Turn off -> Show all map spots
            cleanupFavoritesView();
            const allSpots = mapApi.getSpots();
            await updateMapView({ spotsToDisplay: allSpots });
        }
    });

    function cleanupFavoritesView() {
        showingFavorites = false;
        updateButton();
        updateUrlWithFavorites([]);
        clearAllUI();
        // Restore default cards context (area) when exiting favorites
        (async () => {
            try {
                const { areaSlug } = parseUrlParams();
                const currentArea = areaSlug ? await getAreaBySlug(areaSlug) : null;
                if (currentArea) {
                    // Pass true for animate to respect ONE SOURCE OF TRUTH
                    setCardsContext('area', { areaId: currentArea.id, areaData: currentArea }, true);
                }
            } catch (err) {
                console.warn('Could not restore area cards after exiting favorites:', err);
            }
        })();
    }

    globalFavoritesChangedHandler = async () => {
        if (showingFavorites) {
            // If favorites list changes while active, refetch and update view
            const displayIds = await getFilteredFavoriteIds();
            if (displayIds.length > 0) {
                const spots = await fetchFavoritesWithChunking(displayIds);
                await updateMapView({ spotsToDisplay: spots, shouldAnimate: false, keepFavoritesActive: true });
            } else {
                // No favorites left in this area
                await updateMapView({ spotsToDisplay: [], keepFavoritesActive: true });
                // Don't necessarily turn off the mode, just show empty
            }
            updateUrlWithFavorites(displayIds);
        }
    };

    window.addEventListener('favoritesChanged', globalFavoritesChangedHandler);

    // Set initial state
    updateButton();
}

export function setFavoritesButtonInactive() {
    if (typeof globalSetFavoritesInactive === 'function') {
        globalSetFavoritesInactive();
    }
} 
